// Auto-billing for products with a linked journal (template): turns the
// template journal's medicine entries into sale product lines. A product
// linked to the drug (product.drug === medicine.id) wins; otherwise the
// medicine's own price is used (stamped from drug.prisAUP when the medicine
// was added — see addMedicine.js searchDrugCallback). Unlinked medicines
// without a price are skipped: a 0-price line can't be billed anyway.
// Gated by customer.featureFlags.autoAddJournalMedicineAsProducts — see
// docs/superpowers/specs/2026-08-05-auto-add-journal-medicine-products-design.md
import md5 from 'crypto-js/md5';

import localUtil from './util';

// Accounting defaults for a medicine line that has NO linked product (TICKET-01713).
// Such a line has no product to inherit vat/account from, so every accounting
// integration fell back to 3000/Honorar, and Tripletex/POGO/Fiken invoiced it at
// 25 % while the sale itself totalled it at 0 %. Mirrors backend/lib/segment-accounts.js.
export const UNLINKED_MEDICINE_VAT = 25;
export const DEFAULT_MEDICINE_ACCOUNT = 3003;

export function medicineAccountForCustomer(customer) {
    if (parseInt(customer?.segmentAccountsEnabled, 10) === 1) {
        const override = parseFloat(customer.segmentAccountMedicine);
        if (Number.isFinite(override) && override > 0) {
            return override;
        }
    }
    return DEFAULT_MEDICINE_ACCOUNT;
}

// Tripletex/POGO mirror every line as "<customerId>-<productNumber || id>". An unlinked
// line has no id, so a drug without varenr used to become "<customerId>-undefined" and
// every such medicine collided on one register product. Fall back to a stable per-drug key.
export function unlinkedMedicineProductNumber(medicine = {}) {
    return medicine.productNumber || `drug-${medicine.id}`;
}

export function unlinkedMedicineLineFields(medicine = {}, customer = {}) {
    return {
        vat: UNLINKED_MEDICINE_VAT,
        account: medicineAccountForCustomer(customer),
        productNumber: unlinkedMedicineProductNumber(medicine),
    };
}

// Pure: returns a NEW products array with `qty` added to the unlinked line for the
// same drug + animal, or null when no such line exists.
export function withUnlinkedMedicineQtyAdded(products = [], { drugId, animalId, qty = 1 } = {}) {
    const idx = products.findIndex(p => !p.id && p.drug === drugId && p.animal?.id === animalId);
    if (idx < 0) {
        return null;
    }
    const current = parseFloat(products[idx].qty) || 0;
    const add = parseFloat(qty) || 1;
    return products.map((p, i) => (i === idx ? { ...p, qty: current + add } : p));
}

export async function buildMedicineProductLines({
    medicines = [],
    animal,
    existingProducts = [],
    user = {},
    customer = {},
    productStore,
    productCache = new Map(),
} = {}) {
    const lines = [];
    if (!animal || !animal.id || !productStore) {
        return lines;
    }
    const groupSize = parseInt(animal.groupSize, 10) || 1;
    const animalRef = { id: animal.id, name: animal.name };

    for (let i = 0, l = medicines.length; i < l; i += 1) {
        const medicine = medicines[i];
        const drugId = medicine?.id;
        if (!drugId) {
            continue;
        }
        const qty = (parseFloat(medicine.amountPerAnimal) || 1) * groupSize;

        let product;
        if (productCache.has(drugId)) {
            product = productCache.get(drugId);
        } else {
            const response = await productStore.load({
                query: { drug: drugId },
                limit: 1,
                skipUpdate: true,
            });
            product = (response && response.length > 0) ? response[0] : null;
            productCache.set(drugId, product);
        }

        const createdDate = new Date();

        if (product) {
            const duplicate = existingProducts.find(p => p.id === product.id && p.animal?.id === animal.id)
                || lines.find(p => p.id === product.id && p.animal?.id === animal.id);
            if (duplicate) {
                continue;
            }
            lines.push({
                ...product,
                qty,
                createdDate,
                md5: md5(JSON.stringify({ ...localUtil.safeProductForMd5(product), createdDate, animal: animalRef })).toString(),
                saleUser: user.id,
                saleDepartment: user.currentCustomerDepartment,
                animal: animalRef,
            });
            continue;
        }

        const price = parseFloat(medicine.price) || parseFloat(medicine.prisAUP) || 0;
        if (price <= 0) {
            continue;
        }
        const duplicate = existingProducts.find(p => p.drug === drugId && p.animal?.id === animal.id)
            || lines.find(p => p.drug === drugId && p.animal?.id === animal.id);
        if (duplicate) {
            continue;
        }
        lines.push({
            name: medicine.fullname || medicine.name,
            price,
            qty,
            drug: drugId,
            ...unlinkedMedicineLineFields(medicine, customer),
            createdDate,
            md5: md5(JSON.stringify({ drug: drugId, createdDate, animal: animalRef })).toString(),
            saleUser: user.id,
            saleDepartment: user.currentCustomerDepartment,
            animal: animalRef,
        });
    }
    return lines;
}

export default {
    buildMedicineProductLines,
    medicineAccountForCustomer,
    unlinkedMedicineProductNumber,
    unlinkedMedicineLineFields,
    withUnlinkedMedicineQtyAdded,
};
